import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

const Home = () => {
  const { t } = useTranslation();

  const features = [
    {
      title: 'Desenhar Planta',
      description: 'Crie sua planta baixa manualmente com nosso editor intuitivo',
      icon: '✏️',
      link: '/desenhar-planta',
      color: 'bg-blue-500'
    },
    {
      title: 'Gerar com IA',
      description: 'Descreva sua casa e nossa IA cria a planta automaticamente',
      icon: '🤖',
      link: '/gerar-planta',
      color: 'bg-purple-500'
    },
    {
      title: 'Dashboard',
      description: 'Visualize e gerencie todos os seus projetos',
      icon: '📊',
      link: '/dashboard',
      color: 'bg-green-500'
    },
    {
      title: 'Administração',
      description: 'Painel administrativo com métricas e gestão',
      icon: '⚙️',
      link: '/admin',
      color: 'bg-orange-500'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/50 to-purple-900/50 opacity-50"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 bg-blue-500/20 text-blue-300 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <span className="animate-pulse w-2 h-2 bg-blue-400 rounded-full"></span>
              Plataforma PropTech
            </div>
            
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6">
              Visualize Sua
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">
                Casa em 3D
              </span>
            </h1>
            
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-10">
              Crie plantas baixas, gere visualizações 3D imersivas e transforme suas ideias em realidade.
              Nossa plataforma combina tecnologia de ponta com simplicidade.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/gerar-planta"
                className="px-8 py-4 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-semibold rounded-xl hover:from-blue-600 hover:to-purple-700 transition-all transform hover:scale-105 shadow-lg shadow-blue-500/25"
              >
                🚀 Começar com IA
              </Link>
              <Link
                to="/desenhar-planta"
                className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white font-semibold rounded-xl hover:bg-white/20 transition-all border border-white/20"
              >
                ✏️ Desenhar Manual
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Como Funciona</h2>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Do conceito à visualização 3D em poucos minutos
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((feature) => (
            <Link
              key={feature.link}
              to={feature.link}
              className="group relative bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:border-white/20 transition-all hover:transform hover:-translate-y-2"
            >
              <div className={`w-14 h-14 ${feature.color} rounded-xl flex items-center justify-center text-2xl mb-4 group-hover:scale-110 transition-transform`}>
                {feature.icon}
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">{feature.title}</h3>
              <p className="text-gray-400 text-sm">{feature.description}</p>
              <div className="absolute bottom-4 right-4 text-gray-500 group-hover:text-white transition-colors">
                →
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* Process Steps */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-3xl p-8 md:p-12 border border-white/10">
          <h2 className="text-3xl font-bold text-white text-center mb-12">Fluxo do Cliente</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
            {[
              { step: 1, text: 'Visite o site', icon: '🌐' },
              { step: 2, text: 'Gere planta com IA', icon: '🤖' },
              { step: 3, text: 'Ajuste a planta', icon: '✏️' },
              { step: 4, text: 'Veja em 3D', icon: '🏠' },
              { step: 5, text: 'Receba estimativa', icon: '💰' },
              { step: 6, text: 'Agende consulta', icon: '📅' }
            ].map((item) => (
              <div key={item.step} className="flex flex-col items-center text-center">
                <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center text-2xl mb-3">
                  {item.icon}
                </div>
                <span className="text-blue-400 text-sm font-medium">Passo {item.step}</span>
                <span className="text-white text-sm mt-1">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-white mb-6">
            Pronto para visualizar seu projeto?
          </h2>
          <Link
            to="/gerar-planta"
            className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-semibold rounded-xl hover:from-blue-600 hover:to-purple-700 transition-all transform hover:scale-105 shadow-lg shadow-blue-500/25"
          >
            Começar Agora <span className="text-xl">→</span>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Home;
